<template>
  <div style="padding-top: 30vh">
    <b-row>
      <b-col cols="3"></b-col>

      <b-col class="text-center">
        <img src="@/assets/logo.png" width="100" height="100" />
        <h3>Rejoignez J.L. AI®</h3>
      </b-col>

      <b-col cols="3"></b-col>
    </b-row>

    <b-row v-if="errorMessage" class="mt-4">
      <b-col cols="3"></b-col>

      <b-col>
        <b-alert show variant="danger">
          {{ errorMessage }}
        </b-alert>
      </b-col>

      <b-col cols="3"></b-col>
    </b-row>

    <b-row class="mt-4">
      <b-col cols="3"></b-col>

      <b-col>
        <b-form class="registration" @submit="triggerRegister">
          <b-form-group class="registration__input p-3">
            <b-row>
              <b-col cols="1" class="registration__input-previous">
                <img
                  v-if="currentStep > 0"
                  @click="currentStep--"
                  :src="require(`@/assets/icons/previous.svg`)"
                  class="cursor-pointer"
                  height="20"
                  width="20"
                />
              </b-col>

              <b-col>
                <b-form-input
                  v-if="currentStep === 0"
                  v-model="firstName"
                  @keydown.enter="nextStep"
                  type="text"
                  placeholder="Entrez votre Prénom"
                  required
                ></b-form-input>

                <b-form-input
                  v-else-if="currentStep === 1"
                  v-model="email"
                  @keydown.enter="nextStep"
                  type="email"
                  placeholder="Entrez votre adresse e-mail"
                  required
                ></b-form-input>

                <b-form-input
                  v-else-if="currentStep === 2"
                  v-model="password"
                  @keydown.enter="nextStep"
                  type="password"
                  placeholder="Entrez votre mot de passe"
                  required
                ></b-form-input>

                <b-form-input
                  v-else-if="currentStep === 3"
                  v-model="passwordConfirm"
                  @keydown.enter="nextStep"
                  type="password"
                  placeholder="Confirmez votre mot de passe"
                  required
                ></b-form-input>
              </b-col>

              <b-col cols="1" class="registration__input-next">
                <spinner v-if="isSending" size="sm" />
                <img
                  @click="nextStep"
                  v-else-if="currentStep === 3"
                  :src="require(`@/assets/icons/check-mark.svg`)"
                  class="float-right cursor-pointer"
                  height="20"
                  width="20"
                />
                <img
                  @click="nextStep"
                  v-else
                  :src="require(`@/assets/icons/next.svg`)"
                  class="float-right cursor-pointer"
                  height="20"
                  width="20"
                />
              </b-col>
            </b-row>
          </b-form-group>

          <div v-if="currentStep === 0">
            <div class="input__error" v-if="!$v.firstName.required">Le champ est requis.</div>
            <div class="input__error" v-if="!$v.firstName.minLength">
              Votre prénom doit avoir au moins {{ $v.firstName.$params.minLength.min }} caractères.
            </div>
            <div class="input__error" v-if="!$v.firstName.maxLength">
              Votre prénom doit avoir un maximum de {{ $v.firstName.$params.maxLength.min }} caractères.
            </div>
          </div>

          <div v-if="currentStep === 1">
            <div class="input__error" v-if="!$v.email.required">Le champ est requis.</div>
            <div class="input__error" v-if="!$v.email.email">Cet e-mail n'est pas valide</div>
          </div>

          <div v-if="currentStep === 2">
            <div class="input__error" v-if="!$v.password.required">Le champ est requis.</div>
            <div class="input__error" v-if="!$v.password.minLength">
              Le mot de passe doit avoir au moins {{ $v.password.$params.minLength.min }} caractère.
            </div>
          </div>

          <div v-if="currentStep === 3">
            <div class="input__error" v-if="!$v.passwordConfirm.required">Le champ est requis.</div>
            <div class="input__error" v-if="!$v.passwordConfirm.sameAsPassword">
              Le mot de passe et le mot de passe de confirmation doivent être le même.
            </div>
          </div>
        </b-form>

        <p class="mt-4 text-center">
          <em>Déjà un compte ? <router-link :to="{ name: 'login' }">Connectez-vous</router-link>.</em>
        </p>
      </b-col>

      <b-col cols="3"></b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { REGISTER } from "@/store/actions";

import { BRow, BCol, BForm, BFormGroup, BFormInput, BAlert } from "bootstrap-vue";
import { validationMixin } from "vuelidate";
import { required, email, minLength, maxLength, sameAs } from "vuelidate/lib/validators";

import Spinner from "@/components/Spinner";

export default {
  name: "Register",
  components: { BRow, BCol, BForm, BFormGroup, BFormInput, BAlert, Spinner },
  mixins: [validationMixin],
  computed: {
    ...mapGetters("auth", ["isAuthenticated", "hasErrors"]),
  },
  data: () => ({
    firstName: null,
    email: null,
    password: null,
    passwordConfirm: null,

    currentStep: 0,
    errorMessage: null,

    isSending: false,
  }),
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (vm.isAuthenticated) next({ name: "dashboard" });
    });
  },
  methods: {
    ...mapActions("auth", { register: REGISTER }),
    async triggerRegister(event) {
      if (event) event.preventDefault();

      this.errorMessage = null;
      this.isSending = true;
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.hasSubmitError = true;
        this.isSending = false;
        return;
      }

      const { firstName, email, password } = this;
      const result = await this.register({ firstName, email, password });

      if (this.isAuthenticated) {
        this.$router.push({ name: "dashboard" });
      } else {
        this.errorMessage = result;
        this.isSending = false;
      }
    },
    nextStep(event) {
      event.preventDefault();

      if (this.currentStep === 0) {
        if (this.$v.firstName.required && this.$v.firstName.minLength && this.$v.firstName.maxLength) this.currentStep++;
      } else if (this.currentStep === 1) {
        if (this.$v.email.required && this.$v.email.email) this.currentStep++;
      } else if (this.currentStep === 2) {
        if (this.$v.password.required && this.$v.password.minLength) this.currentStep++;
      } else if (this.currentStep === 3) {
        if (this.$v.passwordConfirm.required && this.$v.passwordConfirm.sameAsPassword) this.triggerRegister();
      }
    },
  },
  validations: {
    firstName: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(100),
    },
    email: {
      required,
      email,
    },
    password: {
      required,
      minLength: minLength(9),
    },
    passwordConfirm: {
      required,
      sameAsPassword: sameAs("password"),
    },
  },
};
</script>

<style lang="scss" scoped>
.registration__input {
  border: 1px solid #292037;
  border-radius: 15px;
  background-color: #211932;
  color: #9593a2;
  font-size: 18px;

  input {
    width: 90%;
    background: transparent;
    border: 0px;
    color: #fff;
    font-size: 18px;
  }

  .registration__input-next {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    transition: transform 0.7s ease-in-out;
  }
}
</style>
